import JSZip from 'jszip';
import Phaser from 'phaser';

type SpriteZipData = {
  atlasData: { textures: Array<{ image: string }> };
  animsData?: SpritePackerAnimationData;
  images: HTMLImageElement[];
  blobUrls: string[];
};

type SpritePackerAnimationData = {
  anims?: Array<Record<string, unknown>>;
  globalTimeScale?: number;
};

type SpriteZipFileConfig = {
  key: string;
  url?: string;
  xhrSettings?: Phaser.Types.Loader.XHRSettingsObject;
};

const ANIMS_SUFFIX = '_anims.json';

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load sprite image: ${url}`));
    image.src = url;
  });
}

function remapAnimationKeys(animsData: SpriteZipData['animsData'], textureKey: string) {
  if (!animsData?.anims) return animsData;

  for (const anim of animsData.anims) {
    const frames = anim.frames as Array<Record<string, unknown>> | undefined;
    if (!frames) continue;
    for (const frame of frames) {
      if (frame && typeof frame === 'object') {
        (frame as { key?: string }).key = textureKey;
      }
    }
  }

  return animsData;
}

function namespaceAnimationKeys(animsData: SpriteZipData['animsData'], namespace: string) {
  if (!animsData?.anims) return animsData;

  for (const anim of animsData.anims) {
    if (!anim || typeof anim !== 'object') continue;
    const animRecord = anim as Record<string, unknown>;
    const key = animRecord.key;
    if (typeof key === 'string') {
      animRecord.key = `${namespace}:${key}`;
    }
    const next = animRecord.next;
    if (typeof next === 'string') {
      animRecord.next = `${namespace}:${next}`;
    }
    const chain = animRecord.chain;
    if (Array.isArray(chain)) {
      animRecord.chain = chain.map((entry) =>
        typeof entry === 'string' ? `${namespace}:${entry}` : entry
      );
    }
  }

  return animsData;
}

function toPhaserAnimationData(
  animsData?: SpritePackerAnimationData
): Phaser.Types.Animations.JSONAnimations | undefined {
  if (!animsData?.anims) return undefined;
  return {
    // Phaser's JSON types are overly strict; the runtime accepts this shape.
    anims: animsData.anims as unknown as Phaser.Types.Animations.JSONAnimation[],
    globalTimeScale: animsData.globalTimeScale ?? 1,
  };
}

async function parseSpriteZip(buffer: ArrayBuffer, textureKey: string): Promise<SpriteZipData> {
  const zip = await JSZip.loadAsync(buffer);

  let atlasData: SpriteZipData['atlasData'] | undefined;
  let animsData: SpriteZipData['animsData'] | undefined;
  const pngBlobs = new Map<string, Blob>();

  for (const [path, zipEntry] of Object.entries(zip.files)) {
    if (zipEntry.dir) continue;
    const filename = path.split('/').pop() || path;

    if (filename.endsWith(ANIMS_SUFFIX)) {
      animsData = JSON.parse(await zipEntry.async('string'));
    } else if (filename.endsWith('.json')) {
      atlasData = JSON.parse(await zipEntry.async('string'));
    } else if (filename.endsWith('.png')) {
      pngBlobs.set(filename, await zipEntry.async('blob'));
    }
  }

  if (!atlasData) {
    throw new Error('No atlas JSON file found in sprite zip.');
  }

  if (!Array.isArray(atlasData.textures)) {
    throw new Error('Invalid atlas JSON data in sprite zip.');
  }

  if (pngBlobs.size === 0) {
    throw new Error('No PNG files found in sprite zip.');
  }

  const images: HTMLImageElement[] = [];
  const blobUrls: string[] = [];

  for (const texture of atlasData.textures) {
    const imageName = texture.image;
    const blob = pngBlobs.get(imageName);

    if (!blob) {
      throw new Error(`Missing texture image "${imageName}" in sprite zip.`);
    }

    const blobUrl = URL.createObjectURL(blob);
    blobUrls.push(blobUrl);
    images.push(await loadImage(blobUrl));
  }

  animsData = remapAnimationKeys(animsData, textureKey);
  animsData = namespaceAnimationKeys(animsData, textureKey);

  return {atlasData, animsData, images, blobUrls};
}

class SpriteZipFile extends Phaser.Loader.File {
  private _spriteZipData?: SpriteZipData;

  constructor(
    loader: Phaser.Loader.LoaderPlugin,
    key: string,
    url?: string,
    xhrSettings?: Phaser.Types.Loader.XHRSettingsObject
  ) {
    super(loader, {
      type: 'spriteZip',
      key,
      url,
      responseType: 'arraybuffer',
      xhrSettings,
    });
  }

  onProcess() {
    this.state = Phaser.Loader.FILE_PROCESSING;

    const buffer = this.xhrLoader?.response as ArrayBuffer | undefined;
    if (!buffer) {
      console.error('Sprite zip did not return any data.');
      this.onProcessError();
      return;
    }

    parseSpriteZip(buffer, this.key)
      .then((spriteZipData) => {
        this._spriteZipData = spriteZipData;
        this.onProcessComplete();
      })
      .catch((error) => {
        console.error(error);
        this.onProcessError();
      });
  }

  addToCache() {
    if (!this._spriteZipData) return;

    const {atlasData, animsData, images, blobUrls} = this._spriteZipData;

    this.loader.textureManager.addAtlasJSONArray(this.key, images, atlasData.textures);

    const phaserAnims = toPhaserAnimationData(animsData);
    if (phaserAnims) {
      this.loader.scene.anims.fromJSON(phaserAnims);
    }

    for (const blobUrl of blobUrls) {
      URL.revokeObjectURL(blobUrl);
    }
  }

  destroy() {
    this._spriteZipData = undefined;
    super.destroy();
  }
}

function spriteZipLoaderCallback(
  this: Phaser.Loader.LoaderPlugin,
  key: string | SpriteZipFileConfig | SpriteZipFileConfig[],
  url?: string,
  xhrSettings?: Phaser.Types.Loader.XHRSettingsObject
) {
  if (Array.isArray(key)) {
    for (const entry of key) {
      this.addFile(new SpriteZipFile(this, entry.key, entry.url, entry.xhrSettings));
    }
  } else if (typeof key === 'object') {
    this.addFile(new SpriteZipFile(this, key.key, key.url, key.xhrSettings));
  } else {
    this.addFile(new SpriteZipFile(this, key, url, xhrSettings));
  }

  return this;
}

export class SpriteZipLoaderPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);
    pluginManager.registerFileType('spriteZip', spriteZipLoaderCallback);
  }
}
